import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language, translations, TranslationKeys } from '@/i18n/translations';
import { getLanguageFromPath, getLocalePrefix, stripLocalePrefix } from '@/hooks/useLocalePath';

const STORAGE_KEY = 'dealigator-language';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectBrowserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return 'en';
  const browserLang = navigator.language || (navigator as any).userLanguage;
  if (!browserLang) return 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();

  if (langCode === 'bg') return 'bg';
  if (langCode === 'ru') return 'ru';
  return 'en';
};

/**
 * Inner provider that has access to router context.
 */
const LanguageProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine language from URL on first render
  const urlLang = getLanguageFromPath(location.pathname);

  const [language, setLanguageState] = useState<Language>(urlLang);

  // On mount: if user visits root "/" with no locale prefix, redirect based on browser/stored language
  useEffect(() => {
    const isRootOrNonLocale = !location.pathname.startsWith('/en') && !location.pathname.startsWith('/ru');

    if (isRootOrNonLocale) {
      // Check stored preference first, then browser detection
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      let detectedLang: Language;

      if (stored && ['en', 'bg', 'ru'].includes(stored)) {
        detectedLang = stored as Language;
      } else {
        detectedLang = detectBrowserLanguage();
      }

      if (detectedLang !== 'bg') {
        // Redirect to the locale-prefixed version
        const basePath = location.pathname === '/' ? '' : location.pathname;
        const newPath = `/${detectedLang}${basePath}${location.search}${location.hash}`;
        navigate(newPath, { replace: true });
        setLanguageState(detectedLang);
        return;
      }
      // bg stays at root, no redirect needed
      setLanguageState('bg');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync language when URL changes (e.g. user navigates)
  useEffect(() => {
    const newLang = getLanguageFromPath(location.pathname);
    if (newLang !== language) {
      setLanguageState(newLang);
    }
  }, [location.pathname, language]);

  const setLanguage = useCallback((lang: Language) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
    // Navigate to the same page but with new locale prefix
    const basePath = stripLocalePrefix(location.pathname);
    const prefix = getLocalePrefix(lang);
    const newPath = basePath === '/' ? (prefix || '/') : `${prefix}${basePath}`;
    navigate(`${newPath}${location.search}${location.hash}`, { replace: true });
    setLanguageState(lang);
  }, [location.pathname, location.search, location.hash, navigate]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Outer provider — must be placed inside BrowserRouter.
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <LanguageProviderInner>{children}</LanguageProviderInner>;
};

const fallback: LanguageContextType = {
  language: 'bg',
  setLanguage: () => {},
  t: translations['bg'],
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  return context ?? fallback;
};
